import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  initPuzzle, moveTile, canMove, isSolved, shuffle,
  saveBest, loadBest, formatTime, getTileColor, SIZES, SIZE_LABELS,
} from './puzzleLogic';
import './Puzzle15.css';

function Puzzle15() {
  const navigate = useNavigate();
  const [size, setSize] = useState(3);
  const [state, setState] = useState(() => initPuzzle(3));
  const timerRef = useRef(null);
  const [touchStart, setTouchStart] = useState(null);

  // Timer
  useEffect(() => {
    if (state.running && !state.solved) {
      timerRef.current = setInterval(() => {
        setState((prev) => ({ ...prev, time: prev.time + 1 }));
      }, 1000);
    }
    return () => clearInterval(timerRef.current);
  }, [state.running, state.solved]);

  const handleTileClick = useCallback((index) => {
    setState((prev) => {
      if (prev.solved) return prev;
      const newTiles = moveTile(index, prev.tiles, prev.size);
      if (!newTiles) return prev;

      const newMoves = prev.moves + 1;
      const solved = isSolved(newTiles, prev.size);
      let bestMoves = prev.bestMoves;
      let bestTime = prev.bestTime;

      if (solved) {
        clearInterval(timerRef.current);
        bestMoves = saveBest(prev.size, 'moves', newMoves);
        bestTime = saveBest(prev.size, 'time', prev.time);
      }

      return {
        ...prev,
        tiles: newTiles,
        moves: newMoves,
        running: true,
        solved,
        bestMoves,
        bestTime,
        history: [...prev.history.slice(-49), prev.tiles],
      };
    });
  }, []);

  const handleUndo = () => {
    setState((prev) => {
      if (prev.history.length === 0 || prev.solved) return prev;
      const last = prev.history[prev.history.length - 1];
      return {
        ...prev,
        tiles: last,
        moves: prev.moves - 1,
        history: prev.history.slice(0, -1),
      };
    });
  };

  const handleShuffle = useCallback((s) => {
    clearInterval(timerRef.current);
    const newSize = s || size;
    setState({
      tiles: shuffle(newSize),
      size: newSize,
      moves: 0,
      time: 0,
      running: false,
      solved: false,
      bestMoves: loadBest(newSize, 'moves'),
      bestTime: loadBest(newSize, 'time'),
      history: [],
    });
  }, [size]);

  const handleSizeChange = (newSize) => {
    setSize(newSize);
    handleShuffle(newSize);
  };

  // Keyboard
  useEffect(() => {
    const handleKey = (e) => {
      setState((prev) => {
        if (prev.solved) return prev;
        const s = prev.size;
        const emptyIndex = prev.tiles.indexOf(0);
        const row = Math.floor(emptyIndex / s);
        const col = emptyIndex % s;
        let targetIndex = -1;

        switch (e.key) {
          case 'ArrowUp': if (row < s - 1) targetIndex = emptyIndex + s; break;
          case 'ArrowDown': if (row > 0) targetIndex = emptyIndex - s; break;
          case 'ArrowLeft': if (col < s - 1) targetIndex = emptyIndex + 1; break;
          case 'ArrowRight': if (col > 0) targetIndex = emptyIndex - 1; break;
          default: return prev;
        }
        e.preventDefault();
        if (targetIndex < 0) return prev;

        const newTiles = moveTile(targetIndex, prev.tiles, s);
        if (!newTiles) return prev;

        const newMoves = prev.moves + 1;
        const solved = isSolved(newTiles, s);
        let bestMoves = prev.bestMoves;
        let bestTime = prev.bestTime;
        if (solved) {
          clearInterval(timerRef.current);
          bestMoves = saveBest(s, 'moves', newMoves);
          bestTime = saveBest(s, 'time', prev.time);
        }

        return {
          ...prev,
          tiles: newTiles,
          moves: newMoves,
          running: true,
          solved,
          bestMoves,
          bestTime,
          history: [...prev.history.slice(-49), prev.tiles],
        };
      });
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, []);

  // Touch/Swipe
  const handleTouchStart = (e) => {
    setTouchStart({ x: e.touches[0].clientX, y: e.touches[0].clientY });
  };

  const handleTouchEnd = (e) => {
    if (!touchStart) return;
    const dx = e.changedTouches[0].clientX - touchStart.x;
    const dy = e.changedTouches[0].clientY - touchStart.y;
    if (Math.max(Math.abs(dx), Math.abs(dy)) < 30) return;

    const s = state.size;
    const emptyIndex = state.tiles.indexOf(0);
    const row = Math.floor(emptyIndex / s);
    const col = emptyIndex % s;
    let targetIndex = -1;

    if (Math.abs(dx) > Math.abs(dy)) {
      if (dx > 0 && col > 0) targetIndex = emptyIndex - 1;
      else if (dx < 0 && col < s - 1) targetIndex = emptyIndex + 1;
    } else {
      if (dy > 0 && row > 0) targetIndex = emptyIndex - s;
      else if (dy < 0 && row < s - 1) targetIndex = emptyIndex + s;
    }
    if (targetIndex >= 0) handleTileClick(targetIndex);
    setTouchStart(null);
  };

  const tileFontSize = size <= 3 ? '2rem' : size === 4 ? '1.5rem' : size === 5 ? '1.2rem' : '1rem';

  return (
    <div className="puzzle-app">
      <div className="puzzle-topbar">
        <div className="puzzle-topbar-left">
          <button className="puzzle-back-btn" onClick={() => navigate('/')}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="puzzle-title">Slide Puzzle</h1>
          <div className="puzzle-size-toggle">
            {SIZES.map((s) => (
              <button
                key={s}
                className={`puzzle-size-btn ${size === s ? 'active' : ''}`}
                onClick={() => handleSizeChange(s)}
              >
                {SIZE_LABELS[s]}
              </button>
            ))}
          </div>
        </div>
        <div className="puzzle-topbar-right">
          <div className="puzzle-scores">
            <div className="puzzle-score-box">
              <span className="puzzle-score-label">Moves</span>
              <span className="puzzle-score-value">{state.moves}</span>
            </div>
            <div className="puzzle-score-box">
              <span className="puzzle-score-label">Time</span>
              <span className="puzzle-score-value">{formatTime(state.time)}</span>
            </div>
          </div>
          <button className="puzzle-action-btn" onClick={handleUndo} disabled={state.history.length === 0 || state.solved} title="Undo">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="1 4 1 10 7 10" />
              <path d="M3.51 15a9 9 0 105.17-9.34L1 10" />
            </svg>
          </button>
          <button className="puzzle-action-btn new" onClick={() => handleShuffle()}>
            New Game
          </button>
        </div>
      </div>

      <div className="puzzle-container">
        <div className="puzzle-instructions">
          <span>Arrange tiles <strong>1–{size * size - 1}</strong> in order. Use <strong>clicks</strong>, <strong>arrow keys</strong>, or <strong>swipe</strong>.</span>
        </div>

        <div
          className="puzzle-board"
          style={{ '--grid-size': size }}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
        >
          {state.tiles.map((tile, index) => {
            const isMovable = canMove(index, state.tiles, size);
            const color = getTileColor(tile);
            return tile === 0 ? (
              <div key="empty" className="puzzle-cell empty"></div>
            ) : (
              <div
                key={tile}
                className={`puzzle-cell tile ${isMovable ? 'movable' : ''} ${state.solved ? 'solved' : ''}`}
                style={{
                  '--tile-color': color,
                  background: color + '22',
                  borderColor: color + '66',
                }}
                onClick={() => handleTileClick(index)}
              >
                <span className="puzzle-tile-number" style={{ color, fontSize: tileFontSize }}>{tile}</span>
              </div>
            );
          })}

          {state.solved && (
            <div className="puzzle-overlay">
              <div className="puzzle-overlay-content">
                <span className="puzzle-win-icon">🎉</span>
                <h2>Solved!</h2>
                <p>{state.moves} moves in {formatTime(state.time)}</p>
                <button className="puzzle-overlay-btn" onClick={() => handleShuffle()}>Play Again</button>
              </div>
            </div>
          )}
        </div>

        {/* Best Scores */}
        <div className="puzzle-bests">
          <div className="puzzle-best-item">
            <span className="puzzle-best-label">Best Moves ({SIZE_LABELS[size]})</span>
            <span className="puzzle-best-value">{state.bestMoves !== null ? state.bestMoves : '—'}</span>
          </div>
          <div className="puzzle-best-item">
            <span className="puzzle-best-label">Best Time ({SIZE_LABELS[size]})</span>
            <span className="puzzle-best-value">{state.bestTime !== null ? formatTime(state.bestTime) : '—'}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Puzzle15;
